/**
 * Shared subscription plan limits configuration (client-side mirror).
 *
 * This must stay in sync with server/services/subscriptionLimits.js.
 * Starter tier = no subscription row (null). We treat null as starter everywhere.
 */

export const STARTER_ROLES = [
  "Charity Head",
  "Operations Head",
  "Project Lead",
  "Project Reporter",
] as const;

export const STANDARD_ROLES = [
  ...STARTER_ROLES,
  "Operations Admin",
] as const;

export const ALL_ROLES = [
  "Charity Head",
  "Operations Head",
  "Operations Admin",
  "Project Lead",
  "Project Reporter",
  "Fundraising Head",
  "Charity Admin",
] as const;

export type PlanName = "starter" | "standard" | "premium";

export interface EffectiveLimits {
  maxActiveProjects: number;
  transactionMode: "manual" | "auto+manual";
  transactionHistoryDays: number;
  aiReportsPerMonth: number;
  allowedRoles: readonly string[];
  usersTotal: number | null;
  usersPerProject: number | null;
  apiAccess: boolean;
  /** Relationships (CRM) - Premium plan only. */
  crmAccess: boolean;
}

export const PLAN_LIMITS = {
  starter: {
    maxActiveProjects: 1,
    transactionMode: "manual" as const,
    transactionHistoryDays: 90,
    aiReportsPerMonth: 0,
    allowedRoles: STARTER_ROLES,
    usersTotal: 5,
    usersPerProject: null,
    apiAccess: false,
    crmAccess: false,
  },
  standard: {
    projectBase: 10,
    priceBasePence: 10000, // £100 in pence
    priceIncrementPence: 5000, // £50 in pence
    projectsPerIncrement: 2,
    transactionMode: "auto+manual" as const,
    transactionHistoryDays: Infinity,
    aiReportsPerMonth: 10,
    allowedRoles: STANDARD_ROLES,
    usersTotal: null,
    usersPerProject: 5,
    apiAccess: false,
    crmAccess: false,
  },
  premium: {
    maxActiveProjects: Infinity,
    transactionMode: "auto+manual" as const,
    transactionHistoryDays: Infinity,
    aiReportsPerMonth: Infinity,
    allowedRoles: ALL_ROLES,
    usersTotal: Infinity,
    usersPerProject: Infinity,
    apiAccess: true,
    crmAccess: true,
  },
};

/**
 * Calculate how many active projects a Standard plan allows based on price paid.
 * Formula: 10 + floor(max(monthlyPrice - 100, 0) / 50) * 2
 *
 * The formula always operates on the monthly-equivalent price.
 * For yearly subscribers, divide by 12 before applying the formula.
 */
export function calculateStandardProjectCap(
  priceAmountPence: number,
  billingInterval: string = "monthly",
): number {
  const { projectBase, priceBasePence, priceIncrementPence, projectsPerIncrement } = PLAN_LIMITS.standard;

  // Normalize yearly price to monthly equivalent
  const monthlyPence = (billingInterval === "yearly" || billingInterval === "year")
    ? Math.round(priceAmountPence / 12)
    : priceAmountPence;

  const extraPence = Math.max(monthlyPence - priceBasePence, 0);
  const steps = Math.floor(extraPence / priceIncrementPence);
  return projectBase + steps * projectsPerIncrement;
}

/**
 * Get the effective limits for a given plan name and price.
 */
export function getEffectiveLimits(
  planName: string | null,
  priceAmountPence: number = 0,
  billingInterval: string = "monthly",
): EffectiveLimits {
  const plan = planName || "starter";

  if (plan === "premium") {
    return {
      maxActiveProjects: Infinity,
      transactionMode: "auto+manual",
      transactionHistoryDays: Infinity,
      aiReportsPerMonth: Infinity,
      allowedRoles: ALL_ROLES,
      usersTotal: Infinity,
      usersPerProject: Infinity,
      apiAccess: true,
      crmAccess: true,
    };
  }

  if (plan === "standard") {
    const maxActiveProjects = calculateStandardProjectCap(priceAmountPence, billingInterval);
    return {
      maxActiveProjects,
      transactionMode: "auto+manual",
      transactionHistoryDays: Infinity,
      aiReportsPerMonth: PLAN_LIMITS.standard.aiReportsPerMonth,
      allowedRoles: STANDARD_ROLES,
      usersTotal: null,
      usersPerProject: PLAN_LIMITS.standard.usersPerProject,
      apiAccess: false,
      crmAccess: false,
    };
  }

  // starter (default)
  return {
    maxActiveProjects: PLAN_LIMITS.starter.maxActiveProjects,
    transactionMode: "manual",
    transactionHistoryDays: PLAN_LIMITS.starter.transactionHistoryDays,
    aiReportsPerMonth: PLAN_LIMITS.starter.aiReportsPerMonth,
    allowedRoles: STARTER_ROLES,
    usersTotal: PLAN_LIMITS.starter.usersTotal,
    usersPerProject: null,
    apiAccess: false,
    crmAccess: false,
  };
}

/**
 * Calculate total user cap for a charity based on plan + active project count.
 */
export function calculateUserCap(planName: string | null, activeProjectCount: number = 0): number {
  const plan = planName || "starter";
  if (plan === "premium") return Infinity;
  if (plan === "standard") return activeProjectCount * PLAN_LIMITS.standard.usersPerProject;
  return PLAN_LIMITS.starter.usersTotal; // starter: 5 total
}

/**
 * Check if a role is allowed for a given plan.
 */
export function isRoleAllowed(planName: string | null, role: string): boolean {
  const plan = planName || "starter";
  if (plan === "premium") return true;
  if (plan === "standard") return (STANDARD_ROLES as readonly string[]).includes(role);
  return (STARTER_ROLES as readonly string[]).includes(role);
}

/**
 * Get the list of allowed roles for a plan.
 */
export function getAllowedRoles(planName: string | null): readonly string[] {
  const plan = planName || "starter";
  if (plan === "premium") return ALL_ROLES;
  if (plan === "standard") return STANDARD_ROLES;
  return STARTER_ROLES;
}
